import { useTranslation } from 'react-i18next';
import { useMemo, useRef, useState } from 'react';
import DropdownMenu from '../../../../core/components/DropdownMenu';
import { withIcon } from '../../../../core/components/Icon';
import { faCheck, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import StringField from '../../../../core/components/Fields/StringField';

export interface ChannelUser {
    email: string;
    firstName: string;
    lastName: string;
}

interface AssignToDropdownProps {
    selectedUser?: string | null;
    channelUsers: ChannelUser[];
    removeSelected: boolean;
    onSelectedUserChange: (emailAddress: string | null, user?: { firstName: string; lastName: string } | null) => void;
}

const SearchIcon = withIcon(faMagnifyingGlass);
const CheckIcon = withIcon(faCheck);

const AssignToDropdown = ({
    selectedUser,
    channelUsers,
    onSelectedUserChange,
    removeSelected,
}: AssignToDropdownProps) => {
    const { t } = useTranslation('assistance');
    const [userSearchValue, setUserSearchValue] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);

    const filteredUsers = useMemo(() => {
        if (!channelUsers) return [];

        if (!userSearchValue) return channelUsers;

        return channelUsers.filter((u: ChannelUser) => {
            const fullName = `${u.firstName} ${u.lastName}`.trim().toLowerCase();
            const email = u.email.toLowerCase();
            const search = userSearchValue.toLowerCase();
            return fullName.includes(search) || email.includes(search);
        });
    }, [channelUsers, userSearchValue]);

    return (
        <>
            <div className="sticky top-0 bg-surface-weak border-b border-line-weak pb-1.5 pt-1.5 z-20">
                <div className="relative px-1.5">
                    <SearchIcon className="absolute pointer-events-none top-1/2 left-3.5 transform -translate-y-1/2 z-10 text-sm text-content-weak" />
                    <StringField
                        value={userSearchValue}
                        onValueChange={setUserSearchValue}
                        placeholder={t('overview.filters.search.placeholder')}
                        inputRef={searchInputRef}
                        className="pl-9"
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </div>
            </div>

            <div className="overflow-y-auto flex-1 px-1.5 py-1">
                {filteredUsers.length > 0 ? (
                    <>
                        {filteredUsers.map((u: ChannelUser) => (
                            <DropdownMenu.Item
                                key={u.email}
                                onClick={() =>
                                    onSelectedUserChange(u.email, { firstName: u.firstName, lastName: u.lastName })
                                }
                                className="justify-between"
                            >
                                <div className="flex flex-col">
                                    <span>{`${u.firstName} ${u.lastName}`.trim() || u.email}</span>
                                    <span className="text-xs text-content-weak">{u.email}</span>
                                </div>
                                {selectedUser === u.email && !removeSelected && (
                                    <CheckIcon className="text-content-brand-strong" />
                                )}
                            </DropdownMenu.Item>
                        ))}
                    </>
                ) : (
                    <div className="p-2 text-content-weak text-sm">{t('overview.filters.noResults')}</div>
                )}
            </div>

            <div className="sticky bottom-0 bg-surface-weak border-t border-line-weak pt-1 pb-1.5 px-1.5 z-20">
                <DropdownMenu.Item
                    className="text-content-strong justify-between"
                    onClick={() => onSelectedUserChange(null, null)}
                >
                    {t('overview.recordRow.actions.notAssigned')}
                    {!selectedUser && !removeSelected && <CheckIcon className="text-content-brand-strong" />}
                </DropdownMenu.Item>
            </div>
        </>
    );
};

export default AssignToDropdown;
